import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Mail, AlertCircle, Loader2, Check, ArrowLeft } from "lucide-react";
import { EMERALD_DARK } from "../../../../constants/colors";
import "../../LoginScreen.css";
import { otpStepSchema, type OtpStepInput } from "../../../../lib/validation/schemas/auth.schema";

interface ForgotOtpStepProps {
  email: string;
  loading: boolean;
  serverError: string;
  onSubmit: (code: string) => void;
  onBack: () => void;
  onResend: () => void;
}

export default function ForgotOtpStep({
  email,
  loading,
  serverError,
  onSubmit,
  onBack,
  onResend,
}: ForgotOtpStepProps) {
  const { t } = useTranslation(["auth", "common"]);
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<OtpStepInput>({
    resolver: zodResolver(otpStepSchema),
    defaultValues: { code: "" },
    mode: "onSubmit",
  });

  const codeValue = watch("code", "");

  const errorMessage = errors.code?.message ?? serverError;

  return (
    <div className="ls-step-container">
      <div className="ls-step-back-wrapper">
        <button
          type="button"
          onClick={onBack}
          className="ls-back-button"
        >
          <ArrowLeft size={16} /> {t("common:actions.back")}
        </button>
      </div>

      <div className="ls-step-icon-circle">
        <Mail size={28} color={EMERALD_DARK} />
      </div>

      <h2 className="ls-step-title">
        {t("forgot.otpTitle")}
      </h2>
      <p className="ls-step-text">
        {t("forgot.otpSubtitle")}
      </p>
      <p className="ls-step-email">
        {email}
      </p>

      {errorMessage && (
        <div className="ls-error-banner ls-error-banner--step">
          <AlertCircle size={16} className="ls-error-icon" />
          <span>{errorMessage}</span>
        </div>
      )}

      <form
        onSubmit={handleSubmit((data) => onSubmit(data.code))}
        className="ls-step-form"
      >
        <div className="ls-input-group">
          <label className="ls-input-label">{t("forgot.resetCodeLabel")}</label>
          <div className="auth-input-container ls-input-container ls-input-container--centered">
            <Controller
              control={control}
              name="code"
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  inputMode="numeric"
                  placeholder="000000"
                  onChange={(e) => field.onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="ls-form-input ls-otp-input"
                  disabled={loading}
                  maxLength={6}
                  autoFocus
                />
              )}
            />
          </div>
        </div>

        <button
          type="submit"
          className={`ls-btn-primary${loading || codeValue.length !== 6 ? " ls-btn-primary--disabled" : ""}`}
          disabled={loading || codeValue.length !== 6}
        >
          {loading ? (
            <Loader2 size={18} className="ls-spinner" />
          ) : (
            <Check size={18} />
          )}
          <span>{loading ? t("common:actions.verifying") : t("common:actions.continue")}</span>
        </button>
      </form>

      <div className="ls-resend-section">
        {t("shared.resendPrompt")}{" "}
        <button
          type="button"
          onClick={onResend}
          disabled={loading}
          className="ls-resend-section__btn"
        >
          {t("shared.resendCode")}
        </button>
      </div>
    </div>
  );
}
